/**
 * Liver Score formula — web mirror (1.5.0) of services/liver/healthScore.ts and
 * functions/src/utils/healthScore.ts. Everything from `export interface
 * CoreNutrients` down is asserted identical across the three copies
 * (functions/test/scoringParity.test.js); edit all three together.
 *
 * The web uses it to re-score a meal after a food edit (./scoring.ts) — the
 * same numbers the server computes. The formula, bands and anchor points are
 * documented in functions/src/utils/healthScore.ts and docs/LIVER_MODEL_SPEC.md §4.
 */

export interface CoreNutrients {
  addedSugarsG: number;
  saturatedFatG: number;
  calories: number;
  sodiumMg: number;
  /** Positive nutrients — accepted for breakdown display, never scored. */
  fiberG?: number;
  proteinG?: number;
}

/**
 * User's daily nutrient budgets. Ceilings (sugar/satFat/calories/sodium) are
 * always required; fiber/protein floors are optional display targets.
 */
export interface DailyLimits {
  calories: number;
  addedSugarG: number;
  saturatedFatG: number;
  sodiumMg: number;
  /** Daily fiber TARGET in grams (floor, not ceiling). Display only. */
  fiberG?: number;
  /** Daily protein TARGET in grams (floor, not ceiling). Display only. */
  proteinG?: number;
  /**
   * Cirrhosis / alcohol-related: protein is a clinical TARGET (1.2–1.5 g/kg,
   * sarcopenia prevention). Changes UI copy ("aim for") — never scoring.
   */
  proteinIsTarget?: boolean;
}

/** Scoring options (kept for parity with the predecessor scorer's V4 hook). */
export interface ScoreOptions {
  /** Count the 3rd-worst ceiling too (half the weight of the 2nd). */
  tertiaryPenalty?: boolean;
}

export type RiskLevel = 'safe' | 'moderate' | 'high';
export type ScoreDriver = 'addedSugar' | 'saturatedFat' | 'calories' | 'sodium' | 'alcohol' | 'caffeine';
export type NutrientLevel = 'low' | 'moderate' | 'high';

export interface ScoreBreakdown {
  score: number;
  riskLevel: RiskLevel;
  driver: ScoreDriver;
  /** Percent of DAILY budget consumed by each nutrient, rounded to 1 decimal */
  percentages: {
    addedSugar: number;
    saturatedFat: number;
    calories: number;
    sodium: number;
    fiber: number | null;
    protein: number | null;
  };
  /** The daily budgets used to compute the percentages, echoed for auditability */
  dailyLimits: {
    calories: number;
    addedSugarG: number;
    saturatedFatG: number;
    sodiumMg: number;
    fiberG: number | null;
    proteinG: number | null;
  };
  /** Per-ceiling coarse level (low/moderate/high) for badge UI */
  levels: {
    addedSugar: NutrientLevel;
    saturatedFat: NutrientLevel;
    calories: NutrientLevel;
    sodium: NutrientLevel;
  };
}

export function scoreFromNutrients(
  nutrients: CoreNutrients,
  dailyLimits: DailyLimits,
  options?: ScoreOptions,
): ScoreBreakdown {
  const sugarPct = pct(nutrients.addedSugarsG, dailyLimits.addedSugarG);
  const satFatPct = pct(nutrients.saturatedFatG, dailyLimits.saturatedFatG);
  const sodiumPct = pct(nutrients.sodiumMg, dailyLimits.sodiumMg);
  const caloriesPctRaw = pct(nutrients.calories, dailyLimits.calories);
  const caloriesScored = caloriesScoredFromPct(caloriesPctRaw);

  // Positive nutrients: % of target, display only.
  const fiberPct =
    dailyLimits.fiberG != null && nutrients.fiberG != null
      ? pct(nutrients.fiberG, dailyLimits.fiberG)
      : null;
  const proteinPct =
    dailyLimits.proteinG != null && nutrients.proteinG != null
      ? pct(nutrients.proteinG, dailyLimits.proteinG)
      : null;

  const sorted = [sugarPct, satFatPct, sodiumPct, caloriesScored].sort((a, b) => b - a);
  const worst = sorted[0];
  const second = sorted[1] ?? 0;
  const third = sorted[2] ?? 0;

  // Driver tie-break: sugar > satFat > calories > sodium (fructose is the most
  // hepatotoxic per calorie; sodium mainly matters for cirrhosis/ascites).
  let driver: ScoreDriver;
  if (sugarPct === worst) driver = 'addedSugar';
  else if (satFatPct === worst) driver = 'saturatedFat';
  else if (caloriesScored === worst) driver = 'calories';
  else driver = 'sodium';

  const secondaryPenalty = 0.2 * Math.max(0, second - 30);
  const tertiaryPenalty = options?.tertiaryPenalty ? 0.1 * Math.max(0, third - 30) : 0;
  const score = Math.round(Math.min(100, Math.max(0, worst + secondaryPenalty + tertiaryPenalty)));
  const riskLevel: RiskLevel = score <= 30 ? 'safe' : score <= 60 ? 'moderate' : 'high';

  return {
    score,
    riskLevel,
    driver,
    percentages: {
      addedSugar: round1(sugarPct),
      saturatedFat: round1(satFatPct),
      // Displayed calorie % is the raw percentage (the soft curve affects the
      // score, never what the user reads).
      calories: round1(caloriesPctRaw),
      sodium: round1(sodiumPct),
      fiber: fiberPct != null ? round1(fiberPct) : null,
      protein: proteinPct != null ? round1(proteinPct) : null,
    },
    dailyLimits: {
      calories: dailyLimits.calories,
      addedSugarG: dailyLimits.addedSugarG,
      saturatedFatG: dailyLimits.saturatedFatG,
      sodiumMg: dailyLimits.sodiumMg,
      fiberG: dailyLimits.fiberG ?? null,
      proteinG: dailyLimits.proteinG ?? null,
    },
    levels: {
      addedSugar: pctToLevel(sugarPct),
      saturatedFat: pctToLevel(satFatPct),
      calories: pctToLevel(caloriesPctRaw),
      sodium: pctToLevel(sodiumPct),
    },
  };
}

/**
 * Calorie soft curve: 0 until 35% of daily (a normal meal), then a 1.3 slope up
 * to 100% of daily, a 0.3 slope beyond, hard-capped at 90 so calories alone can
 * flag a meal HIGH but never claim the maximum score — 100 is reserved for
 * sugar/sat-fat/sodium blowouts and the alcohol override.
 */
function caloriesScoredFromPct(caloriesPct: number): number {
  if (caloriesPct <= 35) return 0;
  const belowDaily = Math.min(caloriesPct, 100) - 35;
  const aboveDaily = Math.max(0, caloriesPct - 100);
  return Math.min(90, belowDaily * 1.3 + aboveDaily * 0.3);
}

function pct(value: number, limit: number): number {
  if (!isFinite(value) || value <= 0) return 0;
  if (!isFinite(limit) || limit <= 0) return 0;
  return (value / limit) * 100;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Map a percentage-of-daily to a coarse level for badge UI. Under 30% is low
 * (less than a third of a day's budget), 30-60% moderate, >60% high.
 */
function pctToLevel(p: number): NutrientLevel {
  if (p < 30) return 'low';
  if (p < 60) return 'moderate';
  return 'high';
}
